import { useEffect } from 'preact/hooks';
import { Signal, effect } from '@preact/signals';

/**
 * Syncs a signal-based activeTab with the URL path.
 * - On mount: reads last segment of URL to set initial tab
 * - On tab change: updates URL via replaceState (no reload)
 * 
 * Usage: useTabRoute(activeTab, '/profile', 'wallet', validTabs)
 */
export function useTabRoute<T extends string>(
  activeTab: Signal<T>,
  basePath: string,
  defaultTab: T,
  validTabs: readonly T[],
) {
  // On mount: read tab from URL
  useEffect(() => {
    const path = window.location.pathname;
    const base = basePath.replace(/\/+$/, '');
    const suffix = path.startsWith(base + '/') ? path.slice(base.length + 1).split('/')[0] : '';
    if (suffix && validTabs.includes(suffix as T)) {
      activeTab.value = suffix as T;
    }
  }, []);

  // On tab change: update URL
  useEffect(() => {
    const dispose = effect(() => {
      const tab = activeTab.value;
      const base = basePath.replace(/\/+$/, '');
      const newPath = tab === defaultTab ? base : `${base}/${tab}`;
      if (window.location.pathname !== newPath) {
        window.history.replaceState(null, '', newPath);
      }
    });
    return dispose;
  }, []);
}
